<template>
  <div>
    <v-container fluid>
      <v-card class="pb-3 mb-3">
        <v-row no-gutters align="center" justify="space-between" class="px-2 py-1">
          <v-col cols="auto">
            <v-card-title class="py-0 my-0 pl-1">{{ title }}</v-card-title>
          </v-col>

          <v-col cols="auto" class="d-flex align-center ga-2 mt-2">
            <v-menu bottom right>
              <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      text
                      v-bind="attrs"
                      v-on="{ ...tooltip, ...menu }"
                      color="blue-grey"
                    >
                      <v-icon large>mdi-history</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("Versions") }}</span>
                </v-tooltip>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, i) in versionList"
                  :key="i"
                  @click="changeVersion(item)"
                >
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn @click="cancel">
              {{ $t("Cancel") }}
            </v-btn>

            <v-btn
              v-if="showAction"
              color="primary"
              @click.prevent="save"
              :loading="saving || loading"
            >
              {{ $t("Save") }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row justify="center" align="center" v-if="loading">
          <div class="text-center">
            <div>
              <v-progress-circular
                class="my-4"
                :size="50"
                width="5"
                color="primary"
                indeterminate
              ></v-progress-circular>
            </div>
            <p class="display-1">{{ $t("Loading") }}...</p>
          </div>
        </v-row>

        <div v-if="!loading">
          <v-row class="my-0 py-0 px-4">
            <v-col cols="3" align-self="center" class="pt-0">
              <v-text-field
                v-bind:label="$t('Name')"
                v-model="contentType.name"
                :error-messages="nameErrors"
                @input="$v.contentType.name.$touch()"
                @blur="$v.contentType.name.$touch()"
              ></v-text-field>
            </v-col>
            <v-col cols="2" align-self="center" class="pt-0">
              <v-switch
                v-model="contentType.encryptDocuments"
                v-bind:label="$t('Encrypted')"
              ></v-switch>
            </v-col>
            <v-col
              :cols="showKeepActualVersion ? '5' : '7'"
              class="text-right"
              align-self="center"
            >
            </v-col>

            <v-col cols="2" class="py-1" v-if="showKeepActualVersion">
              <v-switch
                v-model="contentType.keepActualVersion"
                v-bind:label="$t('KeepActualVersion')"
                dense
                class="mt-0"
              ></v-switch>
            </v-col>
            <v-col cols="6" class="py-1 pt-0">
              <v-textarea
                auto-grow
                no-resize
                :counter="300"
                rows="1"
                v-bind:label="$t('Description')"
                v-model="contentType.description"
                :error-messages="descriptionErrors"
                @input="$v.contentType.description.$touch()"
                @blur="$v.contentType.description.$touch()"
                dense
              ></v-textarea>
            </v-col>
          </v-row>
        </div>
      </v-card>

      <v-card class="py-1">
        <v-card-text class="py-1">
          <v-simple-table v-if="!loading" dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="pa-1" width="40">
                  </th>
                 <th class="text-left px-2 py-0 text-body-2">
                    {{ $t("Code") }}
                  </th>
                  <th class="text-left px-2 py-0 text-body-2">
                    {{ $t("Name") }}
                  </th>
                  <th class="text-left px-2 py-0 text-body-2">
                    {{ $t("Type") }}
                  </th>
                  <th class="text-left px-2 py-0 text-body-2">
                    {{ $t("Readonly") }}
                  </th>
                  <th class="text-left px-2 py-0 text-body-2">
                    {{ $t("Required") }}
                  </th>
                  <th class="text-left px-2 py-0 text-body-2">
                    {{ $t("Visible") }}
                  </th>
                  <th class="text-left px-2 py-0 text-body-2">
                    {{ $t("Unique") }}
                  </th>
                  <th v-if="showAction" class="text-center pa-2">
                    <v-btn small color="primary" @click.prevent="addField">
                      {{ $t("AddField") }}
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                  </th>
                  <th v-if="!showAction" class="text-left pa-2"></th>
                </tr>
              </thead>
              <draggable
                v-model="contentType.fields"
                tag="tbody"
                handle=".draggableItem"
                @sort="onFieldSort"
              >
                <tr v-for="field in contentType.fields" :key="field.code">
                  <td class="pa-1 text-center">
                    <v-icon class="draggableItem" small>mdi-arrow-split-horizontal</v-icon>
                  </td>
                  <td class="pa-2">{{ field.code }}</td>
                  <td class="pa-2">{{ field.name }}</td>
                  <td class="pa-2">{{ getFieldType(field.type) }}</td>
                  <td class="pa-1 text-center">
                    <v-checkbox
                      v-model="field.readOnly"
                      dense
                      hide-details
                      class="ma-0"
                    ></v-checkbox>
                  </td>
                  <td class="pa-1 text-center">
                    <v-checkbox
                      v-model="field.required"
                      @change="fieldRequiredChanged(field)"
                      dense
                      hide-details
                      class="ma-0"
                    ></v-checkbox>
                  </td>
                  <td class="pa-1 text-center">
                    <v-checkbox
                      v-model="field.visible"
                      @change="fieldVisibleChanged(field)"
                      dense
                      hide-details
                      class="ma-0"
                    ></v-checkbox>
                  </td>
                  <td class="pa-1 text-center">
                    <v-checkbox
                      v-model="field.unique"
                      @change="fieldUniqueChanged(field)"
                      dense
                      hide-details
                      class="ma-0"
                    ></v-checkbox>
                  </td>
                  <td class="pa-1 text-center">
                    <div class="d-flex ga-1 justify-center">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            icon
                            small
                            @click="editField(field)"
                            v-if="!isSharedField(field)"
                          >
                            <v-icon color="primary" small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("Edit") }}</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn v-on="on" icon small @click="removeField(field.code)">
                            <v-icon color="red" small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("Delete") }}</span>
                      </v-tooltip>
                    </div>
                  </td>
                </tr>
              </draggable>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-container>
    <FieldModal
      :ref="modalKey"
      @on-saved="onFieldSaved"
      @on-imported="onFieldImported"
      :contentTypeName="contentType.name"
      :contentTypeFields="contentType.fields"
    />
  </div>
</template>
<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import ContentTypeService from '@/services/ContentTypeService';
import ContentType from '@/ecm/models/contentType/ContentType';
import ContentTypeField from '@/ecm/models/contentType/ContentTypeField';
import FieldType from '@/ecm/variables/FieldType';
import FieldModal from '@/components/administration/contentType/FieldModal.vue';
import draggable from 'vuedraggable';
import Utils from '@/ecm/common/Utils';

export default {
    name: 'ContentType',
    components: {
        FieldModal,
        draggable,
    },
    async beforeRouteLeave(to, from, next) {
        if (this.isUnsavedChanges) {
            var res = await this.$confirm(this.$t('UnsavedChangesWarning'), {
                title: this.$t('Confirm'),
                icon: 'mdi-alert',
                color: 'warning',
                buttonTrueText: this.$t('Save'),
                buttonFalseText: this.$t('Cancel'),
            });
            if (res == 1) {
                return this.save();
            } else {
                return next();
            }
        }
        next();
    },
    created() {
        var contentTypeId = this.$route.params.id;
        if (contentTypeId > 0) {
            this.loadContentType(contentTypeId);
            ContentTypeService.getVersions(contentTypeId).then((data) => {
                this.versionList = data;
            });
        }
    },
    data() {
        return {
            versionList: [],
            modalKey: 'modalFieldKey',
            contentType: new ContentType(),
            contentTypeCpy: new ContentType(),
            loading: false,
            saving: false,
        };
    },
    mixins: [validationMixin],
    validations: {
        contentType: {
            name: { required },
            description: {
                maxLength: maxLength(300)
            }
        },
    },
    computed: {
        isUnsavedChanges() {
            if (this.contentTypeCpy != null) {
                const model = Utils.object.toJS(this.contentType);
                const modelCpy = Utils.object.toJS(this.contentTypeCpy);

                return JSON.stringify(model) !== JSON.stringify(modelCpy);
            }

            return false;
        },
        title() {
            if (this.contentType.name) {
                return `${this.$t('ContentType')} - ${this.contentType.name} ${
                    this.contentType.version
                }`;
            } else {
                return `${this.$t('ContentType')}`;
            }
        },
        modalInstance() {
            return this.$refs[this.modalKey];
        },
        isNew() {
            return this.contentType.id == 0;
        },
        showAction() {
            return this.contentType.isLastVersion || this.isNew;
        },
        showVersion() {
            return this.contentType.id > 0 && this.versionList.length > 1;
        },
        showKeepActualVersion() {
            return this.contentType.id > 0 && !this.contentType.hasDocuments;
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.contentType.name.$dirty) return errors;
            !this.$v.contentType.name.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        descriptionErrors() {
            const errors = [];
            if (!this.$v.contentType.description.$dirty) return errors;
            !this.$v.contentType.description.maxLength &&
                errors.push(this.$t('FieldMaxLenght'));
            return errors;
        }
    },
    methods: {
        updateFields(fields) {
            var sortedFields = fields
                .sort(Utils.array.compare('order'))
                .map((f, index) => {
                    f.order = index;
                    return Utils.object.clone(new ContentTypeField(), f);
                });
            this.contentType.fields = sortedFields;
        },
        onFieldSort() {
            const fields = Utils.object.toJS(this.contentType.fields);
            const sortedFields = fields.map((f, index) => {
                f.order = index;
                return f;
            });
            this.updateFields(sortedFields);
        },
        fieldRequiredChanged(field) {
            if (field.required) {
                field.visible = true;
            }
        },
        fieldVisibleChanged(field) {
            if (!field.visible) {
                field.required = false;
                field.readOnly = false;
            }
        },
        async fieldUniqueChanged(field) {
            if (!this.isNew && field.unique) {
                //Check automatic actions and warn the user.
                var alerts = await ContentTypeService.getAlerts(this.contentType.id);
                if (
                    alerts.some((a) =>
                        a.Actions.some((aa) => aa.DefFieldCode == field.code)
                    )
                ) {
                    var confirmMessage = this.$t(
                        'UniqueFieldContainsAutomaticActionsWarning'
                    ).replaceAll('{0}', field.name);

                    var res = await this.$confirm(confirmMessage, {
                        title: this.$t('Confirm'),
                        icon: 'mdi-alert',
                        color: 'warning',
                        buttonTrueText: this.$t('Save'),
                        buttonFalseText: this.$t('Cancel'),
                    });
                    if (res != 1) {
                        field.unique = false;
                    }
                }
            }
        },
        getFieldType(type) {
            switch (type) {
            case FieldType.String:
                return this.$t('FieldTypeString');
            case FieldType.DateTime:
                return this.$t('FieldTypeDate');
            case FieldType.Numeric:
                return this.$t('FieldTypeNumeric');
            case FieldType.StaticList:
                return `${this.$t('FieldTypeList')} (${this.$t('Static')})`;
            case FieldType.DynamicList:
                return `${this.$t('FieldTypeList')} (${this.$t('ExternalTables')})`;
            case FieldType.DynamicTableList:
                return `${this.$t('FieldTypeList')} (${this.$t('InternalTables')})`;
            }
        },
        addField() {
            this.modalInstance.new();
        },
        editField(field) {
            const editField = Utils.object.toJS(field);
            this.modalInstance.edit(editField);
        },
        removeField(fieldCode) {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    const fields = Utils.object.toJS(
                        this.contentType.fields.filter((f) => f.code != fieldCode)
                    );

                    this.updateFields(fields);
                }
            });
        },
        isSharedField(field) {
            return this.isNew && field.id > 0;
        },
        save() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if (this.contentType.fields.length == 0) {
                Utils.toast.error(this.$t('FieldsRequired'));
                return;
            }

            if (this.isNew) {
                this.saveContentType();
            } else {
                this.saving = true;
                ContentTypeService.keepActualVersion(this.contentType.toDto()).then(
                    (result) => {
                        if (result) {
                            this.saveContentType();
                        } else {
                            this.$confirm(this.$t('ContentTypeEditionWarning'), {
                                title: this.$t('Version'),
                                icon: 'mdi-alert',
                                color: 'red',
                                buttonTrueText: this.$t('Save'),
                                buttonFalseText: this.$t('Cancel'),
                            }).then((res) => {
                                if (res == 1) {
                                    this.saveContentType();
                                }
                            });
                        }
                    }
                );
                this.saving = false;
            }
        },
        saveContentType() {
            this.saving = true;
            const contentTypeToSave = this.contentType.toDto();
            ContentTypeService.save(contentTypeToSave)
                .then((result) => {
                    if (result) {
                        this.contentTypeCpy = Utils.object.toJS(this.contentType);
                        if (this.isNew) {
                            Utils.toast.success(this.$t('ContentType') + ' ' + this.$t('CreatedSuccessfully'));
                        } else {
                            Utils.toast.success(this.$t('ContentType') + ' ' + this.$t('UpdatedSuccessfully'));
                        }
                        this.$store.dispatch('account/getUserContentTypes');
                        this.$store.dispatch('account/getUserContentTypeResultsOrders');
                        this.$router.push({
                            name: 'ContentTypeList',
                        });
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.saving = false;
                });
        },
        loadContentType(id) {
            this.loading = true;
            ContentTypeService.get(id)
                .then((dto) => {
                    this.contentType = new ContentType(dto);
                    this.contentTypeCpy = new ContentType(dto);
                })
                .catch(() => {
                    this.$router.push({
                        name: 'ContentTypeList',
                    });
                })
                .then(() => {
                    this.loading = false;
                });
        },
        changeVersion(version) {
            if (this.contentType.id != version.id) {
                this.loadContentType(version.id);
            }
        },
        saveField(field) {
            const fields = Utils.object.toJS(
                this.contentType.fields.filter((f) => f.code != field.code)
            );

            fields.push(field);

            this.updateFields(fields);
        },
        onFieldImported(field) {
            field.id = 0;
            field.order = Number.MAX_VALUE;
            this.saveField(field);
        },
        onFieldSaved(field) {
            if (field.order == -1) {
                field.order = Number.MAX_VALUE;
            }

            if (this.isNew) {
                this.saveField(field);
            } else {
                ContentTypeService.getSharedFields(
                    this.contentType.id,
                    field.code
                ).then((data) => {
                    if (data.length == 0) {
                        this.saveField(field);
                    } else {
                        var relatedContentTypes = data
                            .map(function (item) {
                                return item.text;
                            })
                            .join(',');

                        var message = this.$t('SharedFieldEditConfirm').replace(
                            '{0}',
                            relatedContentTypes
                        );

                        this.$confirm(message, {
                            title: this.$t('Save'),
                            icon: 'mdi-alert',
                            color: 'warning',
                            buttonTrueText: this.$t('Save'),
                            buttonFalseText: this.$t('Cancel'),
                        }).then((res) => {
                            if (res == 1) {
                                this.saveField(field);
                            }
                        });
                    }
                });
            }
        },
        cancel() {
            this.$router.push({
                name: 'ContentTypeList',
            });
        },
    },
};
</script>
